export type ReleasePackageInfo = {
  description: string;
  fullName: string;
  shortName: string;
  title: string;
};

export type ReleaseAnnotation = {
  description: string;
  failureMsg: string;
  file: string;
  fullPath: string;
  packageInfo: ReleasePackageInfo;
  packagePath: string;
  row: number;
  shortName: string;
  title: string;
  warningOrFailure: 'warning' | 'failure';
};

export type ConformaPolicies = {
  releaseAnnotations: {
    [key: string]: ReleaseAnnotation[];
  };
  releasePackages: {
    [key: string]: ReleasePackageInfo;
  };
};

export enum CONFORMA_RESULT_STATUS {
  violations = 'Failed',
  successes = 'Success',
  warnings = 'Warning',
}

export type ConformaRule = {
  metadata: {
    title: string;
    description: string;
    collections: string[];
    code: string;

    effective_on?: string;
    /**
     * Expiry of a policy exception (volatile-config exclusion) covering this rule.
     * Only present when the policy config waives the rule until a given date.
     */
    effective_until?: string;
    solution?: string;
  };
  msg: string;
};

export type ComponentConformaResult = {
  containerImage: string;
  name: string;
  success: boolean;
  violations?: ConformaRule[];
  successes?: ConformaRule[];
  warnings?: ConformaRule[];
};

export type ConformaResult = {
  components: ComponentConformaResult[];
};

export type ConformaResultRow = {
  title: string;
  description: string;
  status: CONFORMA_RESULT_STATUS;
  timestamp?: string;
  component: string;
  msg?: string;
  collection?: string[];
  solution?: string;
  /**
   * Affected image digest(s). Populated with a single-element array for a
   * non-collapsed row, and with every unique digest in the group once
   * `collapseArchDuplicates` has merged arch-duplicate rows together.
   */
  images: string[];
  /** Policy rule code — stable identifier used as primary group key. Optional for backward-compat. */
  code?: string;
  pipelineRunName?: string;
};

export type ComponentConformaStatus = {
  componentName: string;
  status: 'pass' | 'warning' | 'fail' | 'unknown';
  violationCount: number;
  warningCount: number;
  successCount: number;
  pipelineRunName?: string;
};

export type ConformaRefreshState = {
  /** React Query dataUpdatedAt timestamp in ms epoch; 0 when not yet fetched. */
  lastFetchedAt: number;
  /** True while the TaskRun list query is actively re-fetching. */
  isRefreshing: boolean;
  /** Invalidates the TaskRun list query; log queries are unaffected. */
  onRefresh: () => void;
};

export type ApplicationConformaResults = {
  componentStatuses: ComponentConformaStatus[];
  allResults: ConformaResultRow[];
  totalComponents: number;
  totalFailed: number;
  loaded: boolean;
  /**
   * True while fill-in TaskRun queries or per-component log queries are still settling.
   * The tab can render progressive results while this is true.
   */
  settling: boolean;
  /** Fatal errors that prevent the tab from loading (components or TaskRun list). */
  error: unknown;
  /** Non-fatal log fetch failures for one or more components; results may still be partial. */
  partialLogError?: unknown;
  refresh: ConformaRefreshState;
};
